import { useState, useEffect, useRef } from 'react'

interface ImageViewerDialogProps {
  imageUrl: string
  fileName?: string
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  hasPrev?: boolean
  hasNext?: boolean
  currentIndex?: number
  totalCount?: number
  allowDownload?: boolean
  initialRotation?: number // 初始旋转角度（0, 90, 180, 270）
  onRotationChange?: (rotation: number) => void // 旋转变化回调
  // 分享链接相关
  shareLink?: string // 当前图片的分享链接
  onShareLinkChange?: (link: string) => void | Promise<void> // 分享链接变化回调
  onOpenLinkSelector?: () => void // 打开链接选择对话框的回调
  showShareLinkInput?: boolean // 是否显示分享链接输入框（编辑模式）
  shareLinkNotLoggedInMessage?: string // 未登录时的提示信息
  onError?: () => void // 图片加载失败回调
}

/**
 * 增强的图片查看器组件
 * 支持：缩放、旋转、拖拽移动、下载
 */
export const ImageViewerDialog = ({ 
  imageUrl, 
  fileName, 
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  currentIndex,
  totalCount,
  allowDownload = true,
  initialRotation = 0,
  onRotationChange,
  onError,
  shareLink = '',
  onShareLinkChange,
  onOpenLinkSelector,
  showShareLinkInput = false,
  shareLinkNotLoggedInMessage
}: ImageViewerDialogProps) => {
  const [scale, setScale] = useState(1)
  const [rotation, setRotation] = useState(initialRotation)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [showLinkPanel, setShowLinkPanel] = useState(false)
  const [linkInput, setLinkInput] = useState(shareLink)
  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const linkInputRef = useRef<HTMLInputElement>(null)

  const minScale = 0.1
  const maxScale = 5
  const scaleStep = 0.1

  // 重置状态
  useEffect(() => {
    setScale(1)
    setRotation(initialRotation)
    setPosition({ x: 0, y: 0 })
    setLinkInput(shareLink)
    setShowLinkPanel(false)
  }, [imageUrl, initialRotation, shareLink])

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft' && hasPrev && onPrev) {
        e.preventDefault()
        onPrev()
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        e.preventDefault()
        onNext()
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        handleZoomIn()
      } else if (e.key === '-') {
        e.preventDefault()
        handleZoomOut()
      } else if (e.key === 't' || e.key === 'T') {
        e.preventDefault()
        handleRotate()
      } else if (e.key === '0') {
        e.preventDefault()
        handleReset()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, onPrev, onNext, hasPrev, hasNext])

  // 鼠标滚轮缩放
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -scaleStep : scaleStep
      const newScale = Math.max(minScale, Math.min(maxScale, scale + delta))
      setScale(newScale)
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      container.removeEventListener('wheel', handleWheel)
    }
  }, [scale])

  // 拖拽移动（鼠标和触摸）
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
    }
  }

  // 触摸相关状态
  const touchStartRef = useRef({ x: 0, y: 0, distance: 0, scale: 1, centerX: 0, centerY: 0 })

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      // 单指：准备拖拽
      const touch = e.touches[0]
      setIsDragging(true)
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      })
    } else if (e.touches.length === 2) {
      // 双指：准备缩放
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      )
      touchStartRef.current = {
        x: 0,
        y: 0,
        distance,
        scale,
        centerX: (touch1.clientX + touch2.clientX) / 2,
        centerY: (touch1.clientY + touch2.clientY) / 2
      }
      setIsDragging(false)
    }
  }

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        // 单指移动：拖拽
        const touch = e.touches[0]
        setPosition({
          x: touch.clientX - dragStart.x,
          y: touch.clientY - dragStart.y
        })
      } else if (e.touches.length === 2) {
        // 双指移动：缩放
        e.preventDefault()
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        )
        const scaleRatio = currentDistance / touchStartRef.current.distance
        const newScale = Math.max(minScale, Math.min(maxScale, touchStartRef.current.scale * scaleRatio))
        setScale(newScale)
      }
    }

    const handleTouchEnd = () => {
      setIsDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleTouchEnd)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isDragging, dragStart, scale, minScale, maxScale])

  const handleZoomIn = () => {
    setScale(prev => Math.min(maxScale, prev + scaleStep))
  }

  const handleZoomOut = () => {
    setScale(prev => Math.max(minScale, prev - scaleStep))
  }

  const handleRotate = () => {
    setRotation(prev => {
      const newRotation = (prev + 90) % 360
      // 调用回调函数保存旋转信息
      if (onRotationChange) {
        onRotationChange(newRotation)
      }
      return newRotation
    })
  }

  const handleReset = () => {
    setScale(1)
    const resetRotation = 0
    setRotation(resetRotation)
    setPosition({ x: 0, y: 0 })
    // 调用回调函数保存旋转信息
    if (onRotationChange) {
      onRotationChange(resetRotation)
    }
  }

  const handleDownload = async () => {
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName || 'image.png'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('下载图片失败:', error)
      // 如果 fetch 失败，尝试直接打开链接
      window.open(imageUrl, '_blank')
    }
  }

  // 保存分享链接
  const handleSaveShareLink = async () => {
    if (onShareLinkChange) {
      try {
        await onShareLinkChange(linkInput.trim())
        setShowLinkPanel(false)
      } catch (error) {
        // 如果保存失败，不关闭面板，让用户重试
        console.error('保存链接失败:', error)
      }
    }
  }

  // 清除分享链接
  const handleClearShareLink = () => {
    setLinkInput('')
    if (onShareLinkChange) {
      onShareLinkChange('')
    }
    setShowLinkPanel(false)
  }

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center"
      onClick={onClose}
      ref={containerRef}
    >
      {/* 工具栏 */}
      <div
        className="absolute top-2 sm:top-4 left-1/2 -translate-x-1/2 z-10 flex gap-1 sm:gap-2 bg-black bg-opacity-50 rounded-lg px-2 py-1 sm:px-4 sm:py-2 flex-wrap justify-center max-w-[calc(100%-32px)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleZoomOut}
          className="text-white hover:text-gray-300 px-2 py-1 sm:px-3 sm:py-1 rounded transition-colors text-sm sm:text-base"
          title="缩小 (-)"
        >
          −
        </button>
        <span className="text-white text-xs sm:text-sm px-1 sm:px-2 py-1">
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={handleZoomIn}
          className="text-white hover:text-gray-300 px-2 py-1 sm:px-3 sm:py-1 rounded transition-colors text-sm sm:text-base"
          title="放大 (+)"
        >
          +
        </button>
        <div className="w-px bg-white bg-opacity-30 mx-0.5 sm:mx-1" />
        <button
          onClick={handleRotate}
          className="text-white hover:text-gray-300 px-2 py-1 sm:px-3 sm:py-1 rounded transition-colors text-sm sm:text-base"
          title="旋转 (T)"
        >
          ↻
        </button>
        <button
          onClick={handleReset}
          className="text-white hover:text-gray-300 px-2 py-1 sm:px-3 sm:py-1 rounded transition-colors text-sm sm:text-base"
          title="重置 (0)"
        >
          ⟲
        </button>
        {allowDownload && (
          <>
            <div className="w-px bg-white bg-opacity-30 mx-0.5 sm:mx-1" />
            <button
              onClick={handleDownload}
              className="text-white hover:text-gray-300 px-2 py-1 sm:px-3 sm:py-1 rounded transition-colors text-sm sm:text-base"
              title="下载"
            >
              ⬇
            </button>
          </>
        )}
        {showShareLinkInput && (
          <>
            <div className="w-px bg-white bg-opacity-30 mx-0.5 sm:mx-1" />
            <button
              onClick={() => {
                // 如果提供了链接选择器回调，优先使用它
                if (onOpenLinkSelector) {
                  onOpenLinkSelector()
                  return
                }
                // 否则使用原来的输入框方式
                if (!onShareLinkChange && shareLinkNotLoggedInMessage) {
                  alert(shareLinkNotLoggedInMessage)
                  return
                }
                setShowLinkPanel(!showLinkPanel)
              }}
              className={`px-2 py-1 sm:px-3 sm:py-1 rounded transition-colors text-sm sm:text-base ${
                shareLink ? 'text-cyan-400 hover:text-cyan-300' : 'text-white hover:text-gray-300'
              }`}
              title="设置跳转链接"
            >
              🔗
            </button>
          </>
        )}
      </div>

      {/* 分享链接输入面板 */}
      {showLinkPanel && showShareLinkInput && (
        <div
          className="absolute top-16 sm:top-20 left-1/2 -translate-x-1/2 z-20 bg-gray-900 bg-opacity-95 rounded-lg p-4 shadow-xl border border-gray-700 w-[90%] max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-white text-sm mb-3 font-medium">设置跳转链接</div>
          <p className="text-gray-400 text-xs mb-3">
            输入分享链接地址，当此画布被分享时，点击此图片将跳转到链接的画布
          </p>
          <input
            ref={linkInputRef}
            type="text"
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            placeholder="例如: /share/abc123 或完整URL"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-cyan-500 mb-3"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSaveShareLink()
              } else if (e.key === 'Escape') {
                setShowLinkPanel(false)
              }
            }}
          />
          <div className="flex gap-2 items-center">
            {/* 清除链接放最左边 */}
            {shareLink && (
              <button
                onClick={handleClearShareLink}
                className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors"
              >
                清除链接
              </button>
            )}
            {/* 已设置链接时显示打开链接 */}
            {shareLink && (
              <a
                href={shareLink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                打开链接 ↗
              </a>
            )}
            <div className="flex-1" />
            <button
              onClick={() => setShowLinkPanel(false)}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleSaveShareLink}
              className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded transition-colors"
            >
              保存
            </button>
          </div>
        </div>
      )}

      {/* 切换按钮 - 左侧 */}
      {hasPrev && onPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onPrev()
          }}
          onTouchEnd={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onPrev()
          }}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 active:bg-opacity-100 shadow-lg text-gray-700 hover:text-gray-900 transition-all touch-manipulation"
          title="上一个 (←)"
        >
          <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
      )}

      {/* 切换按钮 - 右侧 */}
      {hasNext && onNext && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onNext()
          }}
          onTouchEnd={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onNext()
          }}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 active:bg-opacity-100 shadow-lg text-gray-700 hover:text-gray-900 transition-all touch-manipulation"
          title="下一个 (→)"
        >
          <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      )}

      {/* 关闭按钮 */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        onTouchEnd={(e) => {
          e.stopPropagation()
          e.preventDefault()
          onClose()
        }}
        className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white text-2xl sm:text-3xl hover:text-gray-300 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-10 active:bg-white active:bg-opacity-10 transition-all touch-manipulation"
        title="关闭 (ESC)"
      >
        ×
      </button>

      {/* 图片容器 */}
      <div
        className="max-w-7xl max-h-full p-2 sm:p-4 cursor-move touch-none"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none'
        }}
      >
        <img
          ref={imageRef}
          src={imageUrl}
          alt={fileName || 'Image'}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl select-none"
          style={{
            transform: `scale(${scale}) rotate(${rotation}deg)`,
            transformOrigin: 'center center'
          }}
          draggable={false}
          onError={(e) => {
            console.error('图片加载失败:', imageUrl)
            // 如果父组件提供了onError回调，调用它（用于尝试其他扩展名）
            if (onError) {
              onError()
              return
            }
            // 否则显示占位符
            const target = e.currentTarget
            target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><text x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999">图片加载失败</text></svg>'
          }}
        />
      </div>

      {/* 索引显示 */}
      {currentIndex !== undefined && totalCount !== undefined && (
        <div className="absolute top-14 sm:top-20 left-1/2 -translate-x-1/2 z-10 text-white bg-black bg-opacity-50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm">
          {currentIndex + 1} / {totalCount}
        </div>
      )}

      {/* 操作提示 */}
      <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 text-white bg-black bg-opacity-50 px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg text-[10px] sm:text-xs max-w-[calc(100%-16px)]">
        <div className="flex flex-wrap gap-2 sm:gap-4 justify-center">
          {hasPrev || hasNext ? <span className="hidden sm:inline">← →: 切换</span> : null}
          <span className="hidden sm:inline">滚轮: 缩放</span>
          <span className="sm:hidden">双指: 缩放</span>
          <span>拖拽: 移动</span>
          <span className="hidden sm:inline">+/-: 缩放</span>
          <span className="hidden sm:inline">T: 旋转</span>
          <span className="hidden sm:inline">0: 重置</span>
          <span className="hidden sm:inline">ESC: 关闭</span>
        </div>
      </div>
    </div>
  )
}

export default ImageViewerDialog

